import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "children", "label", "value", "prefixIcon", "suffixIcon", "checkable", "deletable", "clickable", "deleteBtnHasFocus", "isActiveDescendent", "selected", "disabled", "onDelete", "onKeyUp", "onClick", "className", "onCloseRequest"];
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { ThemeProvider } from '@mui/material/styles';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import { createMenuItemsTheme, DELETE_BTN_FOCUS_VISIBLE_CLASS, DELETE_BTN_HAS_FOCUS_VISIBLE_CLASS, ROOT_PREFIX } from './menuItemStyles';
import { isTouchDevices } from '../utils/platformUtils';
import { onKeyPress } from '../utils/wcagUtils';
import { EmbersCheckbox } from '../checkbox';
export var EmbersMenuItem = function EmbersMenuItem(_ref) {
  var _classnames2;
  var id = _ref.id,
    children = _ref.children,
    label = _ref.label,
    value = _ref.value,
    prefixIcon = _ref.prefixIcon,
    suffixIcon = _ref.suffixIcon,
    checkable = _ref.checkable,
    deletable = _ref.deletable,
    _ref$clickable = _ref.clickable,
    clickable = _ref$clickable === void 0 ? true : _ref$clickable,
    deleteBtnHasFocus = _ref.deleteBtnHasFocus,
    isActiveDescendent = _ref.isActiveDescendent,
    _ref$selected = _ref.selected,
    selected = _ref$selected === void 0 ? false : _ref$selected,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    onDelete = _ref.onDelete,
    onKeyUp = _ref.onKeyUp,
    onClick = _ref.onClick,
    className = _ref.className,
    onCloseRequest = _ref.onCloseRequest,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var theme = createMenuItemsTheme();
  var intl = useIntl();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    checked = _useState2[0],
    setChecked = _useState2[1];
  var deleteBtnRef = useRef(null);
  var menuItemRef = useRef(null);
  useEffect(function () {
    if (checkable && !onCloseRequest) {
      console.error('all checkable menu items need `onCloseRequest` prop');
    }
  }, []);
  useEffect(function () {
    setChecked(selected);
  }, [selected]);
  var handleDeleteButtonClick = function handleDeleteButtonClick(e) {
    var _menuItemRef$current, _menuItemRef$current3;
    e.stopPropagation();
    onDelete === null || onDelete === void 0 || onDelete(value);
    var nextFocus = (_menuItemRef$current = menuItemRef.current) === null || _menuItemRef$current === void 0 ? void 0 : _menuItemRef$current.nextSibling;
    if (!nextFocus) {
      var _menuItemRef$current2;
      nextFocus = (_menuItemRef$current2 = menuItemRef.current) === null || _menuItemRef$current2 === void 0 ? void 0 : _menuItemRef$current2.previousSibling;
    }
    menuItemRef.current = nextFocus;
    menuItemRef.current ? (_menuItemRef$current3 = menuItemRef.current) === null || _menuItemRef$current3 === void 0 ? void 0 : _menuItemRef$current3.focus() : onCloseRequest === null || onCloseRequest === void 0 ? void 0 : onCloseRequest(e);
  };
  var handleKeyUp = function handleKeyUp(e) {
    var _menuItemRef$current4, _menuItemRef$current5, _menuItemRef$current6;
    if (deletable && deleteBtnRef.current && !deleteBtnRef.current.hidden) {
      switch (e.key) {
        case 'ArrowRight':
          e.stopPropagation();
          (_menuItemRef$current4 = menuItemRef.current) === null || _menuItemRef$current4 === void 0 || _menuItemRef$current4.classList.add(DELETE_BTN_HAS_FOCUS_VISIBLE_CLASS);
          deleteBtnRef.current.classList.add(DELETE_BTN_FOCUS_VISIBLE_CLASS);
          deleteBtnRef.current.focus();
          break;
        case 'ArrowLeft':
          e.stopPropagation();
          (_menuItemRef$current5 = menuItemRef.current) === null || _menuItemRef$current5 === void 0 || _menuItemRef$current5.classList.remove(DELETE_BTN_HAS_FOCUS_VISIBLE_CLASS);
          deleteBtnRef.current.classList.remove(DELETE_BTN_FOCUS_VISIBLE_CLASS);
          (_menuItemRef$current6 = menuItemRef.current) === null || _menuItemRef$current6 === void 0 || _menuItemRef$current6.focus();
          break;
        default:
          break;
      }
    }
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (deletable && deleteBtnRef.current && !deleteBtnRef.current.hidden) {
      switch (e.key) {
        case 'ArrowUp':
          if (deleteBtnRef.current.classList.contains(DELETE_BTN_FOCUS_VISIBLE_CLASS)) {
            var _menuItemRef$current7;
            (_menuItemRef$current7 = menuItemRef.current) === null || _menuItemRef$current7 === void 0 || _menuItemRef$current7.focus();
          }
          break;
        case 'ArrowDown':
          if (deleteBtnRef.current.classList.contains(DELETE_BTN_FOCUS_VISIBLE_CLASS)) {
            var _menuItemRef$current8;
            (_menuItemRef$current8 = menuItemRef.current) === null || _menuItemRef$current8 === void 0 || _menuItemRef$current8.focus();
          }
          break;
        default:
          break;
      }
    }
  };
  var handleDeleteButtonBlur = function handleDeleteButtonBlur(e) {
    var _deleteBtnRef$current, _menuItemRef$current9;
    (_deleteBtnRef$current = deleteBtnRef.current) === null || _deleteBtnRef$current === void 0 || _deleteBtnRef$current.classList.remove(DELETE_BTN_FOCUS_VISIBLE_CLASS);
    (_menuItemRef$current9 = menuItemRef.current) === null || _menuItemRef$current9 === void 0 || _menuItemRef$current9.classList.remove(DELETE_BTN_HAS_FOCUS_VISIBLE_CLASS);
  };

  // ButtonBase maps Space and Enter keys (keyup and keydown, respectively) to "onClick" but
  //  we want those keys to act differently for multi-select versus single-select menus,
  //  so differentiate here:
  var handleItemClicked = function handleItemClicked(e) {
    if (e.key) {
      if (e.key === ' ') {
        if (checkable && onClick) {
          onClick(e);
        }
      } else if (e.key === 'Enter') {
        if (checkable) {
          if (onCloseRequest) {
            onCloseRequest(e);
          }
        } else {
          if (onClick) {
            onClick(e);
          }
        }
      }
    } else {
      if (onClick) {
        onClick(e);
      }
    }
  };
  var checkIcon;
  if (checkable) {
    checkIcon = /*#__PURE__*/React.createElement(EmbersCheckbox, {
      checked: checked,
      canGetKeyboardFocus: false,
      label: children || label,
      className: "".concat(ROOT_PREFIX, "-checkbox"),
      inputProps: {
        role: 'none',
        'aria-hidden': true,
        'aria-checked': undefined
      },
      isControlled: true
    });
  }
  var deleteButton;
  if (deletable) {
    deleteButton = /*#__PURE__*/React.createElement(ListItemIcon, {
      ref: deleteBtnRef,
      role: "button",
      "aria-label": intl.formatMessage({
        id: 'embers.iconLabel.delete',
        defaultMessage: 'delete'
      }),
      tabIndex: 0,
      onKeyUp: function onKeyUp(e) {
        return onKeyPress(e, [' ', 'Enter'], handleDeleteButtonClick);
      },
      className: classnames("".concat(ROOT_PREFIX, "-delete-icon"), _defineProperty({}, DELETE_BTN_FOCUS_VISIBLE_CLASS, deleteBtnHasFocus)),
      onClick: handleDeleteButtonClick,
      onBlur: handleDeleteButtonBlur
    }, /*#__PURE__*/React.createElement("i", {
      className: "icon-delete"
    }));
  }
  return /*#__PURE__*/React.createElement(ThemeProvider, {
    theme: theme
  }, /*#__PURE__*/React.createElement(MenuItem, _extends({
    id: id,
    role: checkable ? 'checkbox' : 'option',
    ref: menuItemRef,
    disableRipple: true,
    onKeyUp: handleKeyUp,
    onKeyDown: handleKeyDown,
    onClick: handleItemClicked,
    value: value,
    selected: checked,
    "aria-selected": !disabled && !checkable ? checked : undefined,
    "aria-checked": checkable ? checked : undefined,
    disabled: disabled,
    className: classnames((_classnames2 = {}, _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-root"), true), _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-touchDevice"), isTouchDevices()), _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-checkable"), checkable), _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-hasPrefix"), !!prefixIcon), _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-hasSuffix"), !!suffixIcon), _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-deletable"), deletable), _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-clickable"), clickable), _defineProperty(_classnames2, "".concat(ROOT_PREFIX, "-isActiveDescendent"), isActiveDescendent), _defineProperty(_classnames2, DELETE_BTN_HAS_FOCUS_VISIBLE_CLASS, deleteBtnHasFocus), _classnames2), className)
  }, otherProps), !!prefixIcon && /*#__PURE__*/React.createElement(ListItemIcon, {
    className: "".concat(ROOT_PREFIX, "-prefix")
  }, prefixIcon), checkIcon, !checkable && /*#__PURE__*/React.createElement(Typography, {
    align: "left"
  }, children || label), !!suffixIcon && /*#__PURE__*/React.createElement(ListItemIcon, {
    className: "".concat(ROOT_PREFIX, "-suffix")
  }, suffixIcon), deleteButton));
};